/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable no-unused-vars */
'use client'

import * as React from 'react'
import { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'

interface Tab {
  id: string
  label: string
}

interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  tabs: Tab[]
  activeTab?: string
  onTabChange?: (tabId: string) => void
}

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  ({ className, tabs, activeTab, onTabChange, ...props }, ref) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
    const [activeIndex, setActiveIndex] = useState(0)
    const [hoverStyle, setHoverStyle] = useState({})
    const [activeStyle, setActiveStyle] = useState({ left: '0px', width: '0px' })
    const tabRefs = useRef<(HTMLDivElement | null)[]>([])

    // Đồng bộ activeIndex với activeTab
    useEffect(() => {
      if (activeTab) {
        const index = tabs.findIndex((tab) => tab.id === activeTab)
        if (index !== -1) {
          setActiveIndex(index)
        } else {
          setActiveIndex(0) // Mặc định về tab đầu tiên nếu activeTab không khớp
        }
      }
    }, [activeTab, tabs])

    // Cập nhật hover style khi hoveredIndex thay đổi
    useEffect(() => {
      if (hoveredIndex !== null) {
        const hoveredElement = tabRefs.current[hoveredIndex]
        if (hoveredElement) {
          const { offsetLeft, offsetWidth } = hoveredElement
          setHoverStyle({
            left: `${offsetLeft}px`,
            width: `${offsetWidth}px`
          })
        }
      }
    }, [hoveredIndex])

    // Cập nhật active style khi activeIndex thay đổi
    useEffect(() => {
      const activeElement = tabRefs.current[activeIndex]
      if (activeElement) {
        const { offsetLeft, offsetWidth } = activeElement
        setActiveStyle({
          left: `${offsetLeft}px`,
          width: `${offsetWidth}px`
        })
      }
    }, [activeIndex])

    // Khởi tạo active style cho tab đầu tiên
    useEffect(() => {
      requestAnimationFrame(() => {
        const initialIndex = activeTab ? tabs.findIndex((tab) => tab.id === activeTab) : 0
        const firstElement = tabRefs.current[initialIndex >= 0 ? initialIndex : 0]
        if (firstElement) {
          const { offsetLeft, offsetWidth } = firstElement
          setActiveStyle({
            left: `${offsetLeft}px`,
            width: `${offsetWidth}px`
          })
        }
      })
    }, [activeTab, tabs])

    return (
      <div ref={ref} className={cn('relative w-full px-3', className)} {...props}>
        <div className='relative w-full'>
          {/* Hover Highlight */}
          <div
            className='absolute h-[30px] transition-all duration-300 ease-out bg-[#0e0f1114] dark:bg-[#ffffff1a] rounded-3xl flex items-center'
            style={{
              ...hoverStyle,
              opacity: hoveredIndex !== null ? 1 : 0
            }}
          />

          {/* Active Indicator */}
          <div
            className='absolute w-full bottom-[-6px] h-[2px] bg-[#0e0f11] dark:bg-white transition-all duration-300 ease-out'
            style={activeStyle}
          />

          {/* Tabs */}
          <div className='relative w-full flex space-x-3xl items-center'>
            {tabs.map((tab, index) => (
              <div
                key={tab.id}
                ref={(el) => (tabRefs.current[index] = el) as any}
                className={cn(
                  'px-3 py-2 w-full cursor-pointer transition-colors duration-300 h-[30px]',
                  index === activeIndex ?
                    'text-[#0e0e10] dark:text-white'
                  : 'text-[#0e0f1199] dark:text-[#ffffff99]'
                )}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => {
                  setActiveIndex(index)
                  onTabChange?.(tab.id)
                }}
              >
                <div className='text-sm font-medium leading-5 whitespace-nowrap flex items-center justify-center h-full'>
                  {tab.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }
)
Tabs.displayName = 'Tabs'

export { Tabs }
